import { create } from 'zustand'

import * as messageApi from '../api/message.api'

const getPartnerIdString = (partner) => {
  if (!partner) return ''
  return String(partner._id || partner.id || '')
}

export const useMessagesStore = create((set, get) => ({
  messages: [],
  unreadCounts: {
    total: 0,
    bySender: {},
    byRole: { sales: 0, marketing: 0, purchase: 0 },
  },
  activePartner: null,
  isLoading: false,
  isSending: false,
  adminContact: null,

  setActivePartner: (partner) => {
    set({ activePartner: partner })
  },

  fetchConversation: async (partnerId) => {
    if (!partnerId) return []
    const pid = String(partnerId)
    set({ isLoading: true })
    try {
      const messages = await messageApi.getConversation(pid)
      set({
        messages: Array.isArray(messages) ? messages : [],
        isLoading: false,
      })
      return messages
    } catch (error) {
      console.error('Failed to fetch conversation:', error)
      set({ messages: [], isLoading: false })
      throw error
    }
  },

  sendMessage: async ({ recipientId, recipientRole, message }) => {
    if (!message?.trim() || get().isSending) return null
    set({ isSending: true })
    try {
      const newMsg = await messageApi.sendMessage({
        recipientId: String(recipientId),
        recipientRole,
        message: message.trim(),
      })

      if (newMsg) {
        set((state) => {
          const exists = state.messages.some(
            (m) => String(m.id || m._id) === String(newMsg.id || newMsg._id),
          )
          return {
            messages: exists ? state.messages : [...state.messages, newMsg],
            isSending: false,
          }
        })
      } else {
        set({ isSending: false })
      }

      return newMsg
    } catch (error) {
      set({ isSending: false })
      throw error
    }
  },

  markAsRead: async (partnerId, partnerRole = null) => {
    if (!partnerId) return
    const pid = String(partnerId)
    try {
      await messageApi.markMessagesAsRead(pid)

      set((state) => {
        const resolvedRole = partnerRole || state.activePartner?.role
        const isPartnerAdmin = resolvedRole === 'admin'
        let currentBySender = { ...(state.unreadCounts?.bySender || {}) }
        let currentByRole = {
          sales: 0,
          marketing: 0,
          purchase: 0,
          ...(state.unreadCounts?.byRole || {}),
        }
        let newTotal = state.unreadCounts?.total || 0

        if (isPartnerAdmin) {
          // Sales/Marketing user has read Admin's messages -> reset all unread counts
          currentBySender = {}
          newTotal = 0
          currentByRole = { sales: 0, marketing: 0, purchase: 0 }
        } else {
          const unreadForPartner = currentBySender[pid] || 0
          delete currentBySender[pid]
          newTotal = Math.max(0, newTotal - unreadForPartner)
          if (resolvedRole && currentByRole[resolvedRole] !== undefined) {
            currentByRole[resolvedRole] = Math.max(
              0,
              currentByRole[resolvedRole] - unreadForPartner,
            )
          }
        }

        const updatedMessages = state.messages.map((m) =>
          String(m.senderId) === pid ||
          (isPartnerAdmin && m.senderRole === 'admin')
            ? { ...m, isRead: true }
            : m,
        )

        return {
          messages: updatedMessages,
          unreadCounts: {
            total: newTotal,
            bySender: currentBySender,
            byRole: currentByRole,
          },
        }
      })

      // Sync exact unread counts from backend
      get()
        .fetchUnreadCounts()
        .catch(() => {})
    } catch (error) {
      console.error('Failed to mark messages as read:', error)
    }
  },

  fetchUnreadCounts: async () => {
    try {
      const data = await messageApi.getUnreadCounts()
      const formatted = {
        total: data?.total || 0,
        bySender: data?.bySender || {},
        byRole: data?.byRole || { sales: 0, marketing: 0, purchase: 0 },
      }
      set({ unreadCounts: formatted })
      return formatted
    } catch (error) {
      console.error('Failed to fetch unread message counts:', error)
    }
  },

  fetchAdminContact: async () => {
    try {
      const admin = await messageApi.getAdminContact()
      set({ adminContact: admin })
      return admin
    } catch (error) {
      console.error('Failed to fetch admin contact:', error)
    }
  },

  addMessage: (newMsg) => {
    if (!newMsg) return
    set((state) => {
      const msgId = String(newMsg.id || newMsg._id || '')
      const msgSenderId = String(newMsg.senderId || '')
      const msgRecipientId = String(newMsg.recipientId || '')

      const activePid = getPartnerIdString(state.activePartner)
      const isPartnerAdmin = state.activePartner?.role === 'admin'

      const isCurrentConversation =
        activePid &&
        (activePid === msgSenderId ||
          activePid === msgRecipientId ||
          (isPartnerAdmin &&
            (newMsg.senderRole === 'admin' ||
              newMsg.recipientRole === 'admin')))

      const exists = state.messages.some((m) => String(m.id || m._id) === msgId)

      let updatedMessages = state.messages
      if (isCurrentConversation && !exists) {
        updatedMessages = [...state.messages, newMsg]
      }

      // Update unread count only if NOT in current open view
      const currentBySender = { ...(state.unreadCounts?.bySender || {}) }
      const currentByRole = {
        sales: 0,
        marketing: 0,
        purchase: 0,
        ...(state.unreadCounts?.byRole || {}),
      }
      let newTotal = state.unreadCounts?.total || 0

      if (!isCurrentConversation) {
        currentBySender[msgSenderId] = (currentBySender[msgSenderId] || 0) + 1
        newTotal += 1
        const senderRole = newMsg.senderRole
        if (senderRole && currentByRole[senderRole] !== undefined) {
          currentByRole[senderRole] = (currentByRole[senderRole] || 0) + 1
        }
      }

      return {
        messages: updatedMessages,
        unreadCounts: {
          total: newTotal,
          bySender: currentBySender,
          byRole: currentByRole,
        },
      }
    })
  },

  updateMessageRead: ({ readBy, readAt }) => {
    const readByStr = String(readBy || '')
    set((state) => ({
      messages: state.messages.map((m) =>
        String(m.recipientId) === readByStr || m.recipientRole === 'admin'
          ? { ...m, isRead: true, readAt: readAt || m.readAt }
          : m,
      ),
    }))
  },

  clearConversation: () => set({ messages: [], activePartner: null }),
}))
